/** @jsx jsx */
import { useState, Fragment } from "react"
import Carousel from "nuka-carousel"
import { jsx } from "frontend/src/utils/jsx"
import { SectionGrid } from "frontend/src/components/Layout"
import { Stars } from "./StarRating"
import { Logo } from "frontend/src/components/Logo"
import { Icon } from "frontend/src/components/Icon"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"

function TitleDiv({ titleLogo, title, fontColor }) {
  return (
    <div
      sx={{
        mx: "auto",
        mt: 6,
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* {titleLogo && <Logo name={titleLogo} width="70px" height="70px" />} */}
      {title && (
        <h3
          sx={{
            mb: 8,
            fontFamily: "heading",
            fontSize: [7, 7, "38px"],
            lineHeight: ["30px", "30px", "44px"],
            fontWeight: ["medium", "medium", "regular"],
            letterSpacing: "-1.24px",
            maxWidth: ["unset", "500px", "500px", "500px"],
            color: fontColor,
          }}
        >
          {title}
        </h3>
      )}
    </div>
  )
}

function CarouselDesktopDiv({
  slideIndex,
  setSlideIndex,
  allReviews,
  reviewIcons,
  handleSlideShow,
  handleNumberSlideShow,
  verified,
}) {
  const halfLength = allReviews.length / 2
  const indexes = []
  for (let i = 0; i < halfLength; i++) {
    indexes.push(2 * i)
  }
  return (
    <div sx={{ display: ["none", "none", "block"] }}>
      <Carousel
        slideIndex={slideIndex}
        slidesToShow={1}
        enableKeyboardControls={true}
        dragging={true}
        wrapAround={false}
        afterSlide={slideIndex => handleSlideShow(slideIndex)}
        renderBottomCenterControls={({ currentSlide }) => (
          <div
            sx={{
              display: "flex",
              width: "180px",
              justifyContent: "center",
            }}
          >
            <SliderControl
              arr={indexes}
              num={currentSlide}
              slideShowFn={handleSlideShow}
              slideShowFn2={handleNumberSlideShow}
              slideStyles={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                color: "almostBlack",
                width: "20px",
                height: "20px",
                letterSpacing: "0.8192px",
              }}
            />
          </div>
        )}
        renderBottomLeftControls={() => null}
        // Disable of default Previous and Next Button
        renderCenterLeftControls={() => null}
        renderCenterRightControls={() => null}
      >
        {indexes.map(i => {
          const length = allReviews.length
          const currReview = allReviews[i]
          const nextReview = allReviews[(i + 1) % length]
          return (
            <div
              key={i}
              sx={{
                minHeight: "380px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                cursor: "default",
                gap: "64px",
                py: 5,
              }}
            >
              <SingleReview
                review={currReview}
                stars={reviewIcons}
                display="block"
                verified={verified}
              />
              <SingleReview
                review={nextReview}
                stars={reviewIcons}
                display="block"
                verified={verified}
              />
            </div>
          )
        })}
      </Carousel>
    </div>
  )
}

function CarouselMobileDiv({
  slideIndex,
  setSlideIndex,
  allReviews,
  reviewIcons,
  handleSlideShow,
  handleNumberSlideShow,
  verified,
}) {
  const indexes = allReviews.map((item, index) => index)
  return (
    <div sx={{ display: ["block", "block", "none"] }}>
      <Carousel
        slideIndex={slideIndex}
        slidesToShow={1}
        enableKeyboardControls={true}
        dragging={true}
        wrapAround={true}
        afterSlide={slideIndex => handleSlideShow(slideIndex)}
        renderBottomCenterControls={({ currentSlide }) => (
          <div
            sx={{
              display: "flex",
              width: "180px",
              justifyContent: "center",
              px: 8,
            }}
          >
            <SliderControl
              arr={allReviews}
              num={currentSlide}
              slideShowFn={handleSlideShow}
              slideShowFn2={handleNumberSlideShow}
              slideStyles={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                color: "almostBlack",
                width: "20px",
                height: "20px",
                letterSpacing: ["0.4px", "0.8192px"],
              }}
            />
          </div>
        )}
        renderBottomLeftControls={() => null}
        // Disable of default Previous and Next Button
        renderCenterLeftControls={() => null}
        renderCenterRightControls={() => null}
      >
        {indexes.map(i => {
          const currReview = allReviews[i]
          return (
            <div
              key={i}
              sx={{
                minHeight: "350px",
                display: "grid",
                gridTemplateColumns: "1fr",
                cursor: "default",
                gap: "64px",
                py: 8,
                px: 5,
              }}
            >
              <SingleReview
                review={currReview}
                stars={reviewIcons}
                display="block"
                verified={verified}
              />
            </div>
          )
        })}
      </Carousel>
    </div>
  )
}

function CarouselDiv({
  slideIndex,
  setSlideIndex,
  allReviews,
  reviewIcons,
  handleSlideShow,
  handleNumberSlideShow,
  verified,
}) {
  return (
    <Fragment>
      <CarouselDesktopDiv
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        allReviews={allReviews}
        reviewIcons={reviewIcons}
        handleSlideShow={handleSlideShow}
        handleNumberSlideShow={handleNumberSlideShow}
        verified={verified}
      />
      <CarouselMobileDiv
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        allReviews={allReviews}
        reviewIcons={reviewIcons}
        handleSlideShow={handleSlideShow}
        handleNumberSlideShow={handleNumberSlideShow}
        verified={verified}
      />
    </Fragment>
  )
}

function SingleReview({
  review: { title, reviewImage, sourceName, reviewRating, reviewTitle, reviewText },
  stars,
  display,
  verified,
}) {
  const getReviewText = data => {
    const maxLength = 270
    const content = data
      .map(paragraph =>
        paragraph.children.reduce(
          (acc, curr) => (acc.length > 0 ? `${acc} ${curr.text}` : curr.text),
          ""
        )
      )
      .join(" ")
    const extractedText = content.length > maxLength ? `${content.substring(0, 270)} ...` : content // Limit content to 270 characters
    return extractedText
  }
  return (
    <div
      sx={{
        display: display,
        py: 9,
        px: 8,
        height: ["340px", "340px", "300px"],
        backgroundColor: "background",
        color: "almostBlack",
      }}
    >
      <div
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {reviewImage ? (
          <div sx={{ mr: 3 }}>
            <Img
              fluid={getFluidFromAsset(reviewImage, 200)}
              sx={{ width: "49px", height: "49px" }}
            />
          </div>
        ) : (
          <div sx={{ width: 0, height: "49px" }} />
        )}
        <div>
          <p sx={{ fontSize: 3, lineHeight: 1.16, fontFamily: "body", fontWeight: 500 }}>
            <span>{title}</span> <Logo name={verified} width="18px" height="18px" />
          </p>
          <p sx={{ fontSize: 0, fontFamily: "body" }}>über {sourceName}</p>
        </div>
      </div>
      <Stars name={stars} size={[20, 30, 25]} rating={reviewRating} style={{ mt: 4 }}></Stars>
      <div
        sx={{
          color: "almostBlack",
          mt: [5, 4, 6],
          fontSize: 0,
          lineHeight: 1.86,
        }}
      >
        {reviewTitle && reviewTitle.length > 0 && <h4>{reviewTitle}</h4>}
        <p sx={{ fontSize: 0, lineHeight: 1.86 }}>{getReviewText(reviewText)}</p>
      </div>
    </div>
  )
}

function SliderControl({ arr, num, slideShowFn, slideShowFn2, slideStyles }) {
  const results = []
  for (let i = 0, { length } = arr; i < length; i++) {
    results.push(`0${i + 1}`)
  }
  return results.map((elem, ind) => {
    if (elem === `0${num + 1}`) {
      return (
        <div
          key={elem}
          role="button"
          name={ind}
          tabIndex={0}
          sx={{
            outline: "none",
            // borderBottom: "2px solid",
            ...slideStyles,
          }}
        >
          <Icon name="Dot" sx={{ color: "almostBlack", width: "20px", height: "20px" }} />
        </div>
      )
    }

    return (
      <div
        key={elem}
        role="button"
        data-elem={elem}
        name={ind}
        tabIndex={0}
        sx={{ outline: "none", ...slideStyles }}
        onClick={e => slideShowFn2(ind)}
        onKeyPress={e => slideShowFn2(ind)}
      >
        <Icon name="Dot" sx={{ color: "blue_200", width: "20px", height: "20px" }} />
      </div>
    )
  })
}

function SliderReviews({
  title,
  titleLogo,
  productImage,
  reviewIcons,
  totalRating,
  backgroundColor,
  fontColor,
  reviewerIcon,
  allReviews,
  forwardIcon,
  backIcon,
  backgroundIcon,
}) {
  const [slideIndex, setSlideIndex] = useState(0)

  // Slide event handler
  const handleSlideShow = index => {
    // console.log(index)
    setSlideIndex(index)
  }

  const handleNumberSlideShow = e => {
    // console.dir(e)
    // const calIndex = parseInt(e.target.dataset.elem) - 1
    setSlideIndex(e)
  }

  return (
    <SectionGrid
      sx={{
        pt: 8,
        pb: 8,
        position: "relative",
        backgroundColor: backgroundColor,
      }}
    >
      <SectionGrid.wide>
        <TitleDiv
          titleLogo={titleLogo}
          title={title}
          totalRating={totalRating}
          fontColor={fontColor}
        />
        <CarouselDiv
          handleSlideShow={handleSlideShow}
          handleNumberSlideShow={handleNumberSlideShow}
          slideIndex={slideIndex}
          allReviews={allReviews}
          reviewIcons={reviewIcons}
          verified={reviewerIcon}
        />
      </SectionGrid.wide>
    </SectionGrid>
  )
}

export { SliderReviews }
